import * as ApiHelper from "~/common/utils/ApiHelper";
import { naiveTrackCartContents } from "~/app/analytics/ga/abandonment";
import { resolveBulkAddUrl } from "~/utils/url/urlUtils";
import { registerCallProcessor, scheduleCall } from "~/features/cart/cart/api/CartService";
import { getCurrentBrowserTimezone } from "~/common/utils/dateUtils";
import type { PriceDataDTO } from "~/domains/pricing/types/PriceDataDTO";

export interface AddToCartResponseItem {
  pk: number;
  sapMaterialNumber: string;
  designation: string;
  productCode: string;
  packCode: string;
  packSize: number;
  quantity: number;
  netPrice?: PriceDataDTO;
  actualNetPrice?: PriceDataDTO;
  actualSalePrice?: PriceDataDTO;
  totalPrice?: PriceDataDTO;
  priceUnit?: number;
  priceReference?: string;
  source?: string;
  contractNumber?: string;
  contractLine?: string;
  salesCampaignCode?: string;
  specialPrice?: boolean;
}

export interface AddToCartPartApiItem {
  productCode: string;
  quantity: number;
  packageCode: string;
  packageSize: number;
  primaryWarehouseCode: string;
  partTypeCode: string;
  manualUpdated: boolean;
  originCountry: string;
}

export interface AddToCartApiItem {
  productCode: string;
  packageSize?: number;
  packageCode: string;
  quantity: number;

  /**
   * TODO: Kick this out of ADD TO CART API, it's revelant only for quote stuff.
   */
  confirmedQuantity?: number;
  comment?: string;
  selectedDeliveryDate?: string;
  selectedDeliveryDateIsManual: boolean;
  // estimatedDeliveryDate?: string;
  primaryWarehouseCode?: string;
  contractNumber?: string;
  contractLine?: string;
  contractOpenQuantity?: number;
  priceReference?: string;

  // Quote specific fields
  quoteLine?: string;
  quoteNumber?: string;
  quoteScheduleLineNum?: string;
  quoteMinQuantity?: number;
  isBundled?: boolean;

  quoteExpiryDate?: string;
  logisticalComment?: string;
  estimatedShippingDate?: string;

  isRestrictedPackage: boolean;
  orderEntryNumber?: number;

  optionalParts?: AddToCartPartApiItem[];

  customerOrderNumber?: string;
  originCountry: string;

  salesCampaignCode?: string;
}

export function sendAddToCart(
  item: AddToCartApiItem | AddToCartApiItem[],
  skipTracking = false,
): Promise<AddToCartResponseItem[]> {
  return scheduleCall<AddToCartResponseItem[]>("add", {
    items: Array.isArray(item) ? item : [item],
    skipTracking,
  });
}

const apiUrl = resolveBulkAddUrl();
registerCallProcessor({
  type: "add",
  canBeExecuted: (ongoingCalls) => {
    const isDeleteOngoing = ongoingCalls.some((call) => call.type === "delete" || call.type === "deleteCart");
    return !isDeleteOngoing;
  },
  execute: async ({ items, skipTracking }: { items: AddToCartApiItem[]; skipTracking: boolean }) => {
    const body = {
      items,
      userTimeZone: getCurrentBrowserTimezone(),
    };

    const packageToCartForm = JSON.stringify(body);

    const response = await fetch(apiUrl, {
      body: packageToCartForm,
      cache: "no-cache",
      credentials: "same-origin",
      method: "POST",
      mode: "cors",
      headers: {
        dataType: "json",
        "content-type": "application/json",
        CSRFToken: ApiHelper.getCsrfValue(),
      },
      redirect: "follow",
    });

    if (response.redirected) {
      window.location.reload();
    }

    if (!response.ok) {
      throw new Error("Failed to add items to cart");
    }

    window.SCHA.updateMinicart?.();

    if (!skipTracking) {
      naiveTrackCartContents();
    }

    const data = (await response.json()) as { items: AddToCartResponseItem[]; quantityAdjusted: boolean };
    return data.items;
  },
});
